import { useEffect } from "react";

import { AI_TOOL_CALL_HINT_CONTEXT_KEY } from "components/ai/constants.ts";
import type { ToolNameType } from "components/ai/tools/types.ts";
import { useAppDispatch, useAppSelector } from "store/index.ts";
import { selectToolNames } from "store/selectors";
import { aiStateActions } from "store/slices/ai.ts";

/**
 * If tools are in effect (via calls to `useAITool`), this hook adds a ContextItem to
 * the chat context containing instructions to the back end chat model about how it
 * may use tools to fulfill the query.
 */
export const useAIToolHintContext = () => {
  const dispatch = useAppDispatch();
  const toolNames = useAppSelector(selectToolNames);

  useEffect(() => {
    if (toolNames.length) {
      dispatch(
        aiStateActions.setAiChatData({
          id: AI_TOOL_CALL_HINT_CONTEXT_KEY,
          role: "system",
          text: getToolHintContext(toolNames),
          type: "context",
        }),
      );
    } else {
      dispatch(aiStateActions.removeAiChatData(AI_TOOL_CALL_HINT_CONTEXT_KEY));
    }
  }, [dispatch, toolNames]);
};

const getToolHintContext = (toolNames: ToolNameType[]) => {
  return [
    `The following tools are available to you: ${toolNames.join(", ")}.`,
    "When a tool seems like it might be useful to serve the user's request, call it.",
    "Do not ask for confirmation before calling a tool, and do not ask for additional user input.",
    "If you don't have enough information to go on, infer it from surrounding context.",
    "If you think you might want to call the tool after deliberation, don't respond with text, just call the tool.",
    "If you think an action might require multiple steps, call as many tools as you need to in sequence, no need to wait for user input.",
    "Never ask the user to copy/paste anything, if tool calls are available for content creation.",
  ].join("\n");
};
